"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { BlockProps } from "../BlocksManager/registry";
import Link from "next/link";
import {
  ArrowRightCircle,
  Building2,
  ChevronDown,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  User,
} from "lucide-react";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";

const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_API_URL;

const profilMap: Record<string, string> = {
  "chef d'entreprise": "Chef d'entreprise",
  "chef d'entreprises": "Chef d'entreprise",
  rh: "RH",
  cse: "CSE",
  salarié: "Salarié",
  salarie: "Salarié",
  "réseau d'établissements": "Réseau d'établissements",
  "reseau d'etablissements": "Réseau d'établissements",
};

export default function Formulaire({ data }: BlockProps) {
  if (!data) return null;

  const pathname = usePathname();
  const variantKey = String(data.page || "").trim().toUpperCase();

  const optionsMap: Record<string, string[]> = {
    CHEFS_ENTREPRISE_RH_CSE: ["Chef d'entreprise", "RH", "CSE"],
    SALARIES: ["Salarié"],
    RESEAU_ETABLISSEMENTS: ["Réseau d'établissements"],
    CONCEPT: ["Chef d'entreprise", "RH", "CSE", "Salarié", "Réseau d'établissements"],
  };

  const options = optionsMap[variantKey] || [];

  const [formData, setFormData] = useState({
    profil: "",
    entreprise: "",
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    adresse: "",
    sujet: "",
    message: "",
    accepte: false,
  });

  const [addressDetails, setAddressDetails] = useState({
    street: "",
    postcode: "",
    city: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.accepte) {
      alert("Veuillez accepter la politique de confidentialité");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const urlParams = new URLSearchParams(window.location.search);

      const normalizedProfil = (formData.profil || "").toLowerCase().trim();
      const strapiProfil = profilMap[normalizedProfil] || formData.profil;

      const strapiData = {
        data: {
          profil: strapiProfil,
          entreprise: formData.entreprise || "",
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone || "",
          indicatif: "FR",
          adresse: formData.adresse || "",
          street: addressDetails.street || formData.adresse || "",
          ville: addressDetails.city || "",
          code_postale: addressDetails.postcode || "",
          sujet: `[Régalice PauseDéj'] - ${formData.sujet}`,
          message: formData.message,
          acceptePolitique: true,
          utm_source: urlParams.get("utm_source") || "",
          utm_campaign: urlParams.get("utm_campaign") || "",
          utm_content: urlParams.get("utm_content") || "",
          utm_term: urlParams.get("utm_term") || "",
          utm_medium: urlParams.get("utm_medium") || "",
          gclid: urlParams.get("gclid") || "",
        },
      };

      const response = await fetch(`${STRAPI_URL}/demandes-contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(strapiData),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          profil: "",
          entreprise: "",
          nom: "",
          prenom: "",
          email: "",
          telephone: "",
          adresse: "",
          sujet: "",
          message: "",
          accepte: false,
        });
        setAddressDetails({
          street: "",
          postcode: "",
          city: "",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("❌ Erreur Strapi:", errorData);
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("❌ Erreur lors de l'envoi:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="formulaire" className="relative overflow-hidden bg-violet-semi-light px-6 py-16 md:px-10 lg:px-16 lg:py-20">
      <div className="absolute -left-12 top-32 h-36 w-36 rounded-full bg-violet md:h-48 md:w-48" />
      <div className="absolute -left-3 top-1/2 h-12 w-12 rounded-full bg-violet md:h-16 md:w-16" />
      <div className="absolute right-48 top-0 h-24 w-24 rounded-full bg-violet md:h-32 md:w-32" />
      <div className="absolute -bottom-8 right-10 h-20 w-20 rounded-full bg-violet md:h-56 md:w-56" />

      <div className="relative z-10 mx-auto max-w-4xl">
        <h2 className="text-center font-literata text-[34px] font-bold leading-tight text-black lg:text-[56px]">
          {data.titre}
        </h2>

        <form onSubmit={handleSubmit} className="mx-auto mt-10 max-w-3xl">
          <div className="grid grid-cols-2 gap-4 md:gap-5">
            <div className="relative col-span-2 md:col-span-1">
              <Building2 className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />

              <select
                defaultValue=""
                value={formData.profil}
                onChange={(e) => setFormData((prev) => ({ ...prev, profil: e.target.value }))}
                className="h-16 w-full appearance-none rounded-full border-2 border-transparent bg-white pl-14 pr-12 text-[14px] text-text-body outline-none focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              >
                  {!pathname.startsWith("/repas-entreprise") && (
                    <option value="" disabled>
                      Je suis un(e)*
                    </option>
                  )}
                  {options.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              <ChevronDown className="pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 text-black" />
            </div>

            <div className="relative col-span-2 md:col-span-1">
              <Building2 className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              <input
                type="text"
                placeholder="Nom de l'entreprise"
                value={formData.entreprise}
                onChange={(e) => setFormData((prev) => ({ ...prev, entreprise: e.target.value }))}
                className="h-16 w-full rounded-full border-2 border-transparent bg-white pl-14 pr-5 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              />
            </div>

            <div className="relative col-span-1">
              <User className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              <input
                type="text"
                placeholder="Nom"
                value={formData.nom}
                onChange={(e) => setFormData((prev) => ({ ...prev, nom: e.target.value }))}
                className="h-16 w-full rounded-full border-2 border-transparent bg-white pl-14 pr-5 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              />
            </div>

            <div className="relative col-span-1">
              <User className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              <input
                type="text"
                placeholder="Prénom"
                value={formData.prenom}
                onChange={(e) => setFormData((prev) => ({ ...prev, prenom: e.target.value }))}
                className="h-16 w-full rounded-full border-2 border-transparent bg-white pl-14 pr-5 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              />
            </div>

            <div className="relative col-span-1">
              <Mail className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                className="h-16 w-full rounded-full border-2 border-transparent bg-white pl-14 pr-5 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              />
            </div>

            <div className="relative col-span-1">
              <Phone className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              <input
                type="tel"
                placeholder="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData((prev) => ({ ...prev, telephone: e.target.value }))}
                className="h-16 w-full rounded-full border-2 border-transparent bg-white pl-14 pr-5 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              />
            </div>

            <div className="col-span-2">
              <AddressAutocomplete
                value={formData.adresse}
                onChange={(value, details) => {
                  setFormData((prev) => ({ ...prev, adresse: value }));
                  if (details) {
                    setAddressDetails(details);
                  }
                }}
                placeholder="Adresse de livraison"
                required
                className="h-16 w-full rounded-full border-2 border-transparent bg-white pl-14 pr-14 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                iconClassName="text-violet"
                name="adresse"
              />
            </div>

            <div className="relative col-span-2">
              <Pencil className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />

              <select
                defaultValue=""
                value={formData.sujet}
                onChange={(e) => setFormData((prev) => ({ ...prev, sujet: e.target.value }))}
                className="h-16 w-full appearance-none rounded-full border-2 border-transparent bg-white pl-14 pr-12 text-[14px] text-black outline-none focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                required
              >
                <option value="" disabled>
                  Sujet de votre demande
                </option>
                <option value="Demande d'informations sur nos offres">
                  Demande d'informations sur nos offres
                </option>
                <option value="Demande de devis">
                  Demande de devis
                </option>
                <option value="Support client">
                  Support client
                </option>
              </select>

              <ChevronDown className="pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 text-black" />
            </div>

            <div className="relative col-span-2 ">
              <MessageSquare className="pointer-events-none absolute left-5 top-6 h-5 w-5 text-violet" />
              <textarea
                placeholder="Message"
                rows={3}
                value={formData.message}
                onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                className="min-h-[120px] w-full rounded-[32px] border-2 border-transparent bg-white pl-14 pr-5 pt-5 text-[14px] text-black outline-none placeholder:text-gray-500 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
              />
            </div>
          </div>

          <label className="mt-6 flex items-start gap-3 text-sm leading-6 text-black md:text-[14px]">
            <input
              type="checkbox"
              checked={formData.accepte}
              onChange={(e) => setFormData((prev) => ({ ...prev, accepte: e.target.checked }))}
              className="mt-1 h-4 w-4 rounded border-gray-300 accent-[#65A30D]"
              required
            />
            <span>
              J'accepte que mes données soient utilisées pour traiter ma demande
              conformément à la{" "}
              <Link href="/politique-de-confidentialite" className="underline">
                Politique de confidentialité
              </Link>
              .
            </span>
          </label>

          <div className="mt-10 flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-3 rounded-full bg-violet px-10 py-4 text-[18px] font-semibold text-black transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer"}
              <ArrowRightCircle className="h-5 w-5" />
            </button>
          </div>

          {submitStatus === "success" && (
            <div className="mt-6 text-center text-green-600 font-semibold">
              Votre message a été envoyé avec succès !
            </div>
          )}
          {submitStatus === "error" && (
            <div className="mt-6 text-center text-red-600 font-semibold">
              Une erreur est survenue. Veuillez réessayer.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
